/**
 * シフト表(2026-09-12 着手)。現行「【日報2609】コールセンター」のシフト表タブと同じ作り。
 *
 * 1日分 = `shifts/{date}`。行は 席番号／割振(会社)／スタッフ／業務形態／BB ID／稼働時間(9-18 など)。
 * 時間帯は **9:00〜18:30 を 0.5h 刻みの20コマ**で持ち、開始・終了から塗る(現行シートと同じ)。
 *  - 拘束 = 終了 − 開始
 *  - 稼働 = 塗ったコマ数 × 0.5(枠外の時間は数えない。現行シートも同じ)
 *  - 目標取得数 = 稼働 × 想定取得成績(給湯器 1時間2件=2.0件/h、不用品 3時間1件。2026-09-16 発注者指示で不用品を 3時間1件 に変更)
 * 時間帯ごとの稼働人数はこの表から数える(BlueBean のデータには無い)。
 */
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where, documentId, orderBy } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import type { BusinessType, DateStr, Operator, WorkStyle } from '@core/lib/types';

/** 9:00〜18:30 の 0.5h 刻み(現行シートの列と同じ) */
export const SHIFT_SLOTS: number[] = Array.from({ length: 20 }, (_, i) => 9 + i * 0.5);
/**
 * 出勤表の表に出す時間の列(1時間きざみ。2026-09-25 発注者指示「1/2 を削除」)。
 * 中の計算(実稼働・人数)は 30分きざみのまま
 */
export const SHIFT_HOURS: number[] = Array.from({ length: 10 }, (_, i) => 9 + i);
export const SHIFT_START = SHIFT_SLOTS[0]!;
export const SHIFT_END = SHIFT_SLOTS[SHIFT_SLOTS.length - 1]! + 0.5;
/**
 * 想定取得成績(件/h)。給湯器 = 1時間2件(現行シート「修正シフト時間×2.0件」)、
 * **不用品 = 3時間で1件**(仕様書 §5.4 の原価率目標試算と同じ。2026-09-16 発注者指示で揃えた)
 */
export const TARGET_PER_HOUR_BY_BUSINESS: Record<BusinessType, number> = { kyutoki: 2.0, fuyohin: 1 / 3 };
/** 給湯器の係数(画面の説明文用) */
export const TARGET_PER_HOUR = TARGET_PER_HOUR_BY_BUSINESS.kyutoki;

/** BB ID の架電内容。マスタに無いBB IDは給湯器として扱う */
export function businessOfBbId(operators: Pick<Operator, 'bb_accounts'>[], bbId: string | null | undefined): BusinessType {
  const id = (bbId ?? '').trim().toLowerCase();
  if (!id) return 'kyutoki';
  for (const o of operators) {
    const hit = o.bb_accounts.find((a) => a.bb_id.trim().toLowerCase() === id);
    if (hit) return hit.business;
  }
  return 'kyutoki';
}

export type ShiftRow = {
  /** 行を見分けるためのキー(保存しても変わらない) */
  key: string;
  op_id: string;
  bb_id: string;
  name: string;
  work_style: WorkStyle | null;
  /** 割振(その日に架電する会社)。未定は null */
  company_id: string | null;
  /** 席番号(品川のみ。空でよい) */
  seat: string;
  /** 開始・終了(時。0.5刻み)。未提出は null */
  start: number | null;
  end: number | null;
  /** 休憩(h)。現行シートの「休憩」列 */
  break_hours: number;
  /** 本人が提出した時間(h)。管理者が直した結果が start/end。差が「提出 vs 修正」になる */
  submitted_hours: number | null;
  note: string;
};

export type Shift = {
  id: string; // = date
  date: DateStr;
  rows: ShiftRow[];
  updated_by?: string;
  updated_at?: unknown;
};

export function newShiftRowKey(): string {
  return `sh_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** 稼働(h) = 枠(9:00〜18:30)に入っているコマ数 × 0.5 */
export function workedHoursOf(row: Pick<ShiftRow, 'start' | 'end'>): number {
  if (row.start === null || row.end === null) return 0;
  const from = Math.max(row.start, SHIFT_START);
  const to = Math.min(row.end, SHIFT_END);
  return Math.max(0, Math.round((to - from) * 2) / 2);
}

/** 拘束(h) = 終了 − 開始(枠の外も数える) */
export function boundHoursOf(row: Pick<ShiftRow, 'start' | 'end'>): number {
  if (row.start === null || row.end === null) return 0;
  return Math.max(0, Math.round((row.end - row.start) * 2) / 2);
}

/** 目標取得数 = 稼働 × 想定取得成績(給湯器 2.0件/h・不用品 3時間1件)。小数第1位まで */
export function targetCountOf(row: Pick<ShiftRow, 'start' | 'end'>, business: BusinessType = 'kyutoki'): number {
  return Math.round(workedHoursOf(row) * TARGET_PER_HOUR_BY_BUSINESS[business] * 10) / 10;
}

/** そのコマに入っているか */
export function inSlot(row: Pick<ShiftRow, 'start' | 'end'>, slot: number): boolean {
  return row.start !== null && row.end !== null && slot >= row.start && slot + 0.5 <= row.end;
}

/** その1時間のどこかで稼働しているか(表の帯。30分だけでも色を付ける) */
export function inHour(row: Pick<ShiftRow, 'start' | 'end'>, hour: number): boolean {
  return inSlot(row, hour) || inSlot(row, hour + 0.5);
}

/** 時間帯ごとの人数(現行シートの上部と同じ)。company で絞れる */
export function headcountBySlot(rows: ShiftRow[], companyId?: string | null): number[] {
  return SHIFT_SLOTS.map((s) => rows.filter((r) => (companyId === undefined || r.company_id === companyId) && inSlot(r, s)).length);
}

/** 時間帯ごとの人数(1時間きざみ。表の上に出す) */
export function headcountByHour(rows: ShiftRow[], companyId?: string | null): number[] {
  return SHIFT_HOURS.map((h) => rows.filter((r) => (companyId === undefined || r.company_id === companyId) && inHour(r, h)).length);
}

/** 稼働時間の表示(シフトの時間。「9-17.5」。入っていなければ「—」) */
export function shiftTimeText(row: Pick<ShiftRow, 'start' | 'end'>): string {
  return row.start === null || row.end === null ? '—' : `${row.start}-${row.end}`;
}

function normalizeRow(r: ShiftRow): ShiftRow {
  const half = (v: number | null) => (v === null || !Number.isFinite(v) ? null : Math.round(v * 2) / 2);
  const start = half(r.start);
  const end = half(r.end);
  return {
    ...r,
    start,
    end: start !== null && end !== null && end < start ? start : end,
    break_hours: Math.max(0, Math.round((r.break_hours || 0) * 2) / 2),
    submitted_hours: r.submitted_hours === null ? null : Math.max(0, Math.round(r.submitted_hours * 4) / 4),
    seat: (r.seat ?? '').trim(),
    note: (r.note ?? '').trim(),
  };
}

export async function fetchShift(date: DateStr): Promise<Shift | null> {
  const snap = await getDoc(doc(getDb(), 'shifts', date));
  if (!snap.exists()) return null;
  const d = snap.data() as Omit<Shift, 'id'>;
  return { id: snap.id, ...d, rows: (d.rows ?? []).map(normalizeRow) };
}

export async function fetchShifts(from: DateStr, to: DateStr): Promise<Shift[]> {
  const snap = await getDocs(query(collection(getDb(), 'shifts'), where(documentId(), '>=', from), where(documentId(), '<=', to), orderBy(documentId())));
  return snap.docs.map((d) => {
    const v = d.data() as Omit<Shift, 'id'>;
    return { id: d.id, ...v, rows: (v.rows ?? []).map(normalizeRow) };
  });
}

export async function saveShift(date: DateStr, rows: ShiftRow[], userId: string): Promise<void> {
  const clean = rows.map(normalizeRow).filter((r) => r.op_id !== '');
  await setDoc(doc(getDb(), 'shifts', date), { date, rows: clean, updated_by: userId, updated_at: serverTimestamp() }, { merge: false });
  await writeAuditLog({ targetType: 'import', targetId: `shifts/${date}`, action: 'shift-update', by: userId, changes: [`シフト ${clean.length}名`] });
}
